import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { logClientAudit } from '../lib/audit'
import type { Gateway } from '../types'

export function useGateways(siteId?: string) {
  const [gateways, setGateways] = useState<Gateway[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!siteId) { setGateways([]); setLoading(false); return }
    setLoading(true)
    const { data } = await supabase
      .from('gateways')
      .select('*')
      .eq('site_id', siteId)
      .order('name')
    setGateways((data ?? []) as Gateway[])
    setLoading(false)
  }, [siteId])

  useEffect(() => { load() }, [load])

  const createGateway = useCallback(async (gw: {
    site_id: string
    name: string
    droplet_ip: string
    go2rtc_port?: number
    router_ip?: string
    tunnel_ip?: string
    carrier?: string
    sim_only?: boolean
  }) => {
    const { data, error } = await supabase
      .from('gateways')
      .insert(gw)
      .select()
      .single()
    if (error) throw error
    logClientAudit({ action: 'create', entity_type: 'gateway', entity_id: (data as Gateway).id, entity_name: gw.name })
    await load()
    return data as Gateway
  }, [load])

  const updateGateway = useCallback(async (id: string, updates: Partial<Omit<Gateway, 'id' | 'site_id' | 'created_at'>>) => {
    const { error } = await supabase
      .from('gateways')
      .update(updates)
      .eq('id', id)
    if (error) throw error
    logClientAudit({ action: 'update', entity_type: 'gateway', entity_id: id, details: updates as Record<string, unknown> })
    await load()
  }, [load])

  const deleteGateway = useCallback(async (id: string) => {
    const gw = gateways.find(g => g.id === id)
    const { error } = await supabase
      .from('gateways')
      .delete()
      .eq('id', id)
    if (error) throw error
    logClientAudit({ action: 'delete', entity_type: 'gateway', entity_id: id, entity_name: gw?.name })
    await load()
  }, [load, gateways])

  return { gateways, loading, reload: load, createGateway, updateGateway, deleteGateway }
}
